import React, { useState, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { useSnackbar } from 'components/common/SnackbarNotification';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useAddonRequests, useEnumsByGroup } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
// ============================================================================
// TYPES
// ============================================================================

interface AddonSummaryRecord {
  id: string;
  requestRef: string;
  customerName: string;
  loanType: string;
  accountNumber: string;
  requestedAmount: number;
  eligibleAmount: number;
  maxPermissible: number;
  status: string;
  submittedBy: string;
  createdAt: string;
}

// ============================================================================
// MAIN SCREEN
// ============================================================================

const AddonSummaryReportScreen: React.FC = () => {
  const { showMessage } = useSnackbar();

  // API hooks
  const { data: apiData, loading: apiLoading, error: apiError, refetch } = useAddonRequests();
  const { data: loanTypeEnums } = useEnumsByGroup('LOAN_TYPE');
  const loanTypeLookup = useMemo(() => {
    const map: Record<string, string> = {};
    loanTypeEnums.forEach(e => { map[e.enumKey] = e.enumValue; });
    return map;
  }, [loanTypeEnums]);
  // Data from API
  const data = (apiData as unknown as AddonSummaryRecord[]) || [];
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const searchable = `${row.requestRef} ${row.customerName} ${row.loanType} ${row.accountNumber} ${row.status} ${row.submittedBy}`.toLowerCase();
        if (!searchable.includes(q)) return false;
      }
      if (statusFilter && row.status !== statusFilter) return false;
      if (dateFrom) {
        const rowDate = row.createdAt.split('T')[0];
        if (rowDate < dateFrom) return false;
      }
      if (dateTo) {
        const rowDate = row.createdAt.split('T')[0];
        if (rowDate > dateTo) return false;
      }
      return true;
    });
  }, [data, searchQuery, statusFilter, dateFrom, dateTo]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<AddonSummaryRecord>[] = useMemo(() => [
    { id: 'requestRef', header: 'Request Ref', accessorKey: 'requestRef', size: 160 },
    { id: 'customerName', header: 'Customer', accessorKey: 'customerName', size: 160 },
    {
      id: 'loanType',
      header: 'Loan Type',
      accessorKey: 'loanType',
      size: 140,
      cell: ({ getValue }: any) => {
        const key = getValue();
        return <span>{loanTypeLookup[key] || key}</span>;
      },
    },
    { id: 'accountNumber', header: 'Account #', accessorKey: 'accountNumber', size: 140 },
    {
      id: 'requestedAmount',
      header: 'Requested Amt',
      accessorKey: 'requestedAmount',
      size: 150,
      cell: ({ getValue }: any) => <span style={{ fontWeight: 600 }}>{formatCurrency(getValue())}</span>,
    },
    {
      id: 'eligibleAmount',
      header: 'Eligible Amt',
      accessorKey: 'eligibleAmount',
      size: 140,
      cell: ({ getValue }: any) => <span style={{ fontWeight: 600 }}>{formatCurrency(getValue())}</span>,
    },
    {
      id: 'maxPermissible',
      header: 'Max Permissible',
      accessorKey: 'maxPermissible',
      size: 150,
      cell: ({ getValue }: any) => <span style={{ fontWeight: 600 }}>{formatCurrency(getValue())}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 140,
      cell: ({ getValue }: any) => <span style={getStatusBadgeStyle(getValue())}>{(getValue() ?? '').replace(/_/g, ' ')}</span>,
    },
    {
      id: 'submittedBy',
      header: 'Submitted By',
      accessorKey: 'submittedBy',
      size: 140,
      cell: ({ row }: any) => row.original.submittedByName || row.original.submittedBy,
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], []);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      variant: 'icon',
      tooltip: 'Refresh',
      onClick: () => refetch(),
    },
    {
      id: 'export',
      icon: <IconSystem.download size={16} />,
      label: 'Export',
      variant: 'primary',
      tooltip: 'Export Report',
      onClick: () => showMessage('Export functionality will be available after API integration.', 'info'),
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Add-On Request Summary"
        icon={<IconSystem.file size={20} />}
        description="Summary of all add-on requests by status, customer, and date range."
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search requests...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        actions={actionButtons}
      />

      <div className="module-content">
        {apiError && data.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load report data</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}
        {apiLoading && data.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <SyutGrid
              data={filteredData}
              columns={tableColumns}
              enableRowSelection={false}
              enablePagination
              enableColumnFilters={false}
              emptyMessage="No add-on requests match the current filters."
            />
          </div>
        </div>
        )}

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'RETURNED', label: 'Returned' },
                  { value: 'REJECTED', label: 'Rejected' },
                ]}
                value={statusFilter}
                onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                placeholder="Search status..."
                isSearchable={true}
                isClearable={false}
                size="sm"
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date From</label>
              <input
                type="date"
                className="form-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.75rem' }}>Date To</label>
              <input
                type="date"
                className="form-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setStatusFilter(''); setDateFrom(''); setDateTo(''); }}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddonSummaryReportScreen;
